import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/crm/RichTextEditor";
import { FileText, Plus, PencilSimple, Trash, ArrowLeft, Sparkle } from "@phosphor-icons/react";

const VARS = [
  ["{{contact_first_name}}", "Prénom du décideur"],
  ["{{contact_name}}", "Nom complet du décideur"],
  ["{{contact_title}}", "Poste du décideur"],
  ["{{company_name}}", "Entreprise"],
  ["{{role_title}}", "Poste recherché"],
  ["{{city}}", "Ville de la mission"],
  ["{{consultant_name}}", "Nom du consultant"],
  ["{{consultant_role}}", "Rôle du consultant"],
  ["{{consultant_skills}}", "Compétences du consultant"],
  ["{{sender_name}}", "Votre nom"],
];

export default function EmailTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const importLibrary = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/crm/templates/import-library", {});
      toast.success(data.imported ? `${data.imported} modèle(s) importé(s) ✨` : "Bibliothèque déjà importée");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/crm/templates"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setName(""); setSubject(""); setBodyHtml("<p>Bonjour {{contact_first_name}},</p><p></p><p>Bien cordialement,<br/>{{sender_name}}</p>"); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setName(t.name); setSubject(t.subject || ""); setBodyHtml(t.body_html || ""); setOpen(true); };

  const save = async () => {
    if (!name.trim()) { toast.error("Donnez un nom à votre modèle."); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), subject: subject.trim(), body_html: bodyHtml };
      if (editing) await api.patch(`/crm/templates/${editing.id}`, payload);
      else await api.post("/crm/templates", payload);
      toast.success(editing ? "Modèle mis à jour" : "Modèle créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    try { await api.delete(`/crm/templates/${t.id}`); toast.success("Modèle supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const insertVar = (v) => setBodyHtml((b) => (b || "") + " " + v);

  return (
    <div className="max-w-3xl" data-testid="email-templates-page">
      <Link to="/app/settings/email" className="text-xs text-muted hover:text-fg flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Réglages email</Link>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="overline mb-2">CRM · Prospection</div>
          <h1 className="font-display text-3xl font-bold">Modèles d'emails</h1>
          <p className="text-muted text-sm mt-1">Créez des messages réutilisables avec des variables qui se remplissent automatiquement à l'envoi.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={importLibrary} disabled={importing} data-testid="template-import-library-btn"
            className="flex items-center gap-2 text-sm border border-accent/40 text-accent font-semibold px-4 py-2 rounded-md hover:bg-accent/10 disabled:opacity-60">
            <Sparkle size={15} weight="fill" /> {importing ? "Import…" : "Importer la bibliothèque"}
          </button>
          <button onClick={openCreate} data-testid="template-create-btn"
            className="flex items-center gap-2 text-sm bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift">
            <Plus size={15} weight="bold" /> Nouveau modèle
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center" data-testid="templates-empty">
          <FileText size={38} weight="duotone" className="text-accent mx-auto mb-3" />
          <div className="font-display font-bold text-fg mb-1">Aucun modèle pour l'instant</div>
          <p className="text-muted text-sm">Créez votre premier modèle d'email de prospection.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="templates-list">
          {items.map((t) => (
            <div key={t.id} data-testid={`template-row-${t.id}`}
              className="surface rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-display font-bold text-fg">{t.name}</div>
                <div className="text-sm text-fg/70 mt-0.5 truncate">{t.subject || <span className="text-muted italic">Sans objet</span>}</div>
                <div className="text-xs text-muted mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: t.body_html }} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}
                  className="p-2 rounded-md text-muted hover:text-fg hover:bg-surface-2"><PencilSimple size={16} /></button>
                <button onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}
                  className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2"><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border max-w-2xl" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Créer ou modifier un modèle d'email</DialogDescription>
          <div className="space-y-3">
            <div>
              <label className="overline block mb-1.5">Nom du modèle</label>
              <Input className="bg-surface-2 border-border" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Approche décideur DevOps" data-testid="template-name" />
            </div>
            <div>
              <label className="overline block mb-1.5">Objet</label>
              <Input className="bg-surface-2 border-border" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Consultant {{consultant_name}} disponible" data-testid="template-subject" />
            </div>
            <div>
              <label className="overline block mb-1.5">Message</label>
              <RichTextEditor value={bodyHtml} onChange={setBodyHtml} testid="template-body" />
            </div>
            <div>
              <div className="overline mb-1.5">Variables disponibles (cliquez pour insérer)</div>
              <div className="flex flex-wrap gap-1.5">
                {VARS.map(([v, label]) => (
                  <button key={v} onClick={() => insertVar(v)} title={label} data-testid={`template-var-${v}`}
                    className="text-[11px] font-mono bg-surface-2 border border-border rounded px-1.5 py-0.5 text-accent hover:border-accent/40">{v}</button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="text-sm border border-border rounded-md px-4 py-2 text-muted hover:text-fg">Annuler</button>
              <button onClick={save} disabled={saving} data-testid="template-save-btn"
                className="text-sm bg-accent text-black font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
